import { defaultScheduler, notifyManager } from '@morfi/backend-sdk-v2/client';

import { logger } from '@/lib/telemetry/logger';

// Every SDK/TanStack cache notification re-renders through useSyncExternalStore,
// which React always schedules at SyncLane, and a SyncLane update discards any
// in-progress transition render. A live page (market detail: realtime trades,
// quotes, positions) notifies faster than the next route can render, so the
// Next.js navigation transition restarts until React force-expires it after
// 5 s (MOR-2223). Holding notifications from a route-changing link click until
// the new route commits gives that transition an uninterrupted main thread; the
// leaving page only stops updating for the length of the navigation.
const MAX_HOLD_MS = 5_000;

let installed = false;
let held: (() => void)[] | null = null;
let heldFromPathname = '';
let holdTimer: ReturnType<typeof setTimeout> | undefined;

export function installRouteChangeNotifyScheduler(): () => void {
  installed = true;
  notifyManager.setScheduler(scheduleNotification);
  return (): void => {
    installed = false;
    releaseRouteChangeNotifyHold();
    notifyManager.setScheduler(defaultScheduler);
  };
}

/** No-op outside the app shell, which owns the release on route commit. */
export function holdNotifyUntilRouteCommit(): void {
  if (!installed || held !== null) return;
  held = [];
  heldFromPathname = window.location.pathname;
  holdTimer = setTimeout(() => {
    logger.warn('navigation.notify_hold_timeout', {
      holdMs: MAX_HOLD_MS,
      heldNotifications: held?.length ?? 0,
    });
    releaseRouteChangeNotifyHold();
  }, MAX_HOLD_MS);
}

export function releaseNotifyHoldOnRouteCommit(committedPathname: string): void {
  if (committedPathname !== heldFromPathname) releaseRouteChangeNotifyHold();
}

function releaseRouteChangeNotifyHold(): void {
  if (held === null) return;
  const callbacks = held;
  held = null;
  clearTimeout(holdTimer);
  for (const callback of callbacks) defaultScheduler(callback);
}

function scheduleNotification(callback: () => void): void {
  if (held === null) defaultScheduler(callback);
  else held.push(callback);
}
